import React from "react";
import { Dropdown, MenuProps, message } from "antd";
import { observer } from "mobx-react-lite";
import OrderDTO from "@/dto/order/OrderDTO";
import { useGlobalStore, useOrderMarkStore } from "@/store/hooks";
import { getAntdMarkIcon } from "@/services/api/setting/orderMark";
import useDeleteMarkRequest from "./hooks/useDeleteMarkRequest";
import useAddMarkRequest from "./hooks/useAddMarkRequest";
import { useHistory } from "react-router";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";

enum MarkMenuKey {
    REMOVE = "REMOVE",
    SETTING = "SETTING",
}

export interface OrderMarkButtonProps {
    order: OrderDTO;

    onUpdateOrder?: (orderId: number, props: any) => void;
}

const OrderMarkButton: React.FC<OrderMarkButtonProps> = function (props) {
    const { order, onUpdateOrder } = props;
    const orderMarksStore = useOrderMarkStore();
    const globalStore = useGlobalStore();
    const history = useHistory();
    const { runAsync: runDeleteMark, loading } = useDeleteMarkRequest();
    const { runAsync: runAddMark, loading: loading2 } = useAddMarkRequest();

    async function loadOrderMark(visible) {
        if (!orderMarksStore.isInit && visible) {
            await orderMarksStore.loadList();
        }
    }

    const handleMenuClick: MenuProps["onClick"] = async ({ key }) => {
        if (key === MarkMenuKey.REMOVE) {
            await runDeleteMark(order);

            message.success("删除订单标记成功");

            const childOrders = order.childOrders?.map((childOrder) => ({
                ...childOrder,
                markId: null,
            }));

            onUpdateOrder?.(order.id, { markId: null, childOrders });
            return;
        }

        if (key === MarkMenuKey.SETTING) {
            history.push("/setting/orderMark");
            return;
        }

        const markId = Number(key);
        if (Number.isNaN(markId)) return;

        await runAddMark(order, markId);

        message.success("设置订单标记成功");

        const childOrders = order.childOrders?.map((childOrder) => ({
            ...childOrder,
            markId,
        }));

        onUpdateOrder?.(order.id, { markId, childOrders });

        try {
            const shop = globalStore.shopMapping.get(order.shopId);
            collectEvent(DataTrackEvent.MARK_ORDER, {
                order_count: 1,
                shop_id: order.shopId,
                shop_name: shop.name,
                shop_type: ShopTypeNameMap[shop.type],
            });
        } catch {}
    };

    const items: MenuProps["items"] = [
        ...orderMarksStore.listSkipDeleted.map(({ id, remark, icon, color }) => {
            const IconComponent = getAntdMarkIcon(icon);

            return {
                key: String(id),
                label: (
                    <span>
                        {IconComponent ? <IconComponent style={{ color }} className="mr5" /> : null}
                        {remark}
                    </span>
                ),
            };
        }),
        ...(orderMarksStore.listSkipDeleted.length ? [{ type: "divider" as const }] : []),
        {
            key: MarkMenuKey.REMOVE,
            label: "删除订单标记",
        },
        {
            type: "divider",
        },
        {
            key: MarkMenuKey.SETTING,
            label: (
                <>
                    <p>点击这里可设置标记</p>
                    <p className="text-gray">(可设置颜色、标记备注)</p>
                </>
            ),
        },
    ];

    return (
        <Dropdown
            menu={{ items, onClick: handleMenuClick }}
            trigger={["click"]}
            destroyOnHidden
            onOpenChange={loadOrderMark}
            disabled={loading || loading2}
        >
            <a
                href="#"
                className="text-light link-btn ml5"
                onClick={(event) => {
                    event.preventDefault();
                }}
            >
                标记
            </a>
        </Dropdown>
    );
};

export default observer(OrderMarkButton);
